import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Button
} from "@chakra-ui/react";
import React, {createContext, FC, useCallback, useContext, useRef, useState} from "react";

type ConfirmOptions = {
    title?: string
    body?: React.ReactNode
    confirmText?: string
    cancelText?: string
    isDestructive?: boolean
}

type ConfirmDialogContextI = {
    confirm: (options: ConfirmOptions) => Promise<boolean>
}

const defaultOptions: ConfirmOptions = {
    title: "Please confirm",
    body: "Are you sure?",
    confirmText: "Confirm",
    cancelText: "Cancel",
    isDestructive: false
};

const ConfirmDialogContext = createContext<ConfirmDialogContextI>({
    confirm: async () => false
});

export const useConfirmDialog = () => {
    return useContext(ConfirmDialogContext);
}

export const ConfirmDialogProvider: FC<{children: any}> = ({children}) => {
    const cancelRef = useRef<HTMLButtonElement>(null);
    const resolverRef = useRef<((value: boolean) => void) | undefined>(undefined);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [options, setOptions] = useState<ConfirmOptions>(defaultOptions);

    const confirm = useCallback((nextOptions: ConfirmOptions) => {
        setOptions({
            ...defaultOptions,
            ...nextOptions
        });
        setIsOpen(true);
        return new Promise<boolean>((resolve) => {
            resolverRef.current = resolve;
        });
    }, []);

    const closeWith = (value: boolean) => {
        setIsOpen(false);
        if (resolverRef.current) {
            resolverRef.current(value);
            resolverRef.current = undefined;
        }
    };

    return (
        <ConfirmDialogContext.Provider value={{confirm}}>
            {children}
            <AlertDialog
                isOpen={isOpen}
                leastDestructiveRef={cancelRef}
                onClose={() => closeWith(false)}
                isCentered
            >
                <AlertDialogOverlay/>
                <AlertDialogContent borderRadius="lg" boxShadow="xl" border="1px solid" borderColor="gray.200">
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                        {options.title}
                    </AlertDialogHeader>
                    <AlertDialogBody>
                        {options.body}
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={cancelRef} onClick={() => closeWith(false)}>
                            {options.cancelText}
                        </Button>
                        <Button
                            colorScheme={options.isDestructive ? "red" : "blue"}
                            onClick={() => closeWith(true)}
                            ml={3}
                        >
                            {options.confirmText}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </ConfirmDialogContext.Provider>
    );
};
